import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Experiment, GrowthBook, JSONValue, Result, WidenPrimitives } from '@growthbook/growthbook';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, EMPTY, filter, firstValueFrom, of, Subject, switchMap } from 'rxjs';
import { catchError, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { ExperimentalReportType } from 'src/app/pages/experimental-reports/reports';
import { BillingService } from 'src/app/services/billing/billing.service';
import { DownloadService } from 'src/app/services/download/download.service';
import { ExperimentProps } from 'src/app/services/growthbook/types';
import { DeviceDetector } from 'src/app/services/segment/device-detector';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { AuthState } from 'src/app/store/auth/auth.state';
import { environment } from 'src/environments/environment';

export type ExperimentalReportFeature = `experimental-${ExperimentalReportType}` | 'all-experimental-reports';
export type DashboardFeature = 'executive-dashboard' | 'executive-dashboard-banner';
export type GrowthBookFeature =
  'alpha'
  | 'beta'
  | 'test'
  | 'invite-page-experience'
  | 'surface-invite-button-anywhere'
  | 'company-selector-in-profile-menu'
  | 'new-billing-page'
  | 'user-created-tasks'
  | 'sso-workos'
  | 'gt2-2924'
  | 'tokens-manager'
  | 'new-hours-tracked-report'
  | 'edit-time-snackbar'
  | 'enable-capture2'
  | 'mcp-enabled'
  | 'amplitude-tracking-flag'
  | 'amplitude-guide-flag'
  | 'amplitude-dev-logger-flag'
  | 'user-onboarding-permissions'
  | ExperimentalReportFeature
  | DashboardFeature
  | (string & {});

const clientKey = environment.growthbookClientKey;

@Injectable({
  providedIn: 'root',
})
export class GrowthBookService implements OnDestroy {
  trackExperiment = (_exp: Experiment<any>, { value }: Result<ExperimentProps>) => {
    this.segmentService.trackExperiment(value);
  };

  readonly instance = new GrowthBook({
    enabled: !!environment.growthbookClientKey,
    enableDevMode: !environment.production,
    apiHost: environment.growthbookApiHost,
    clientKey,
    trackingCallback: this.trackExperiment,
  });
  deployment = environment.deployment;
  initialized = new BehaviorSubject<boolean>(false);
  initializedPromise = firstValueFrom(this.initialized.pipe(filter(x => x)));

  private rerenderCount = new BehaviorSubject<number>(0);
  private readonly destroy$ = new Subject<void>();

  /**
   * Evaluated feature values, memoized for the lifetime of one GrowthBook "generation".
   *
   * The SDK re-evaluates every targeting rule on each `getFeatureValue` call; there is no
   * caching inside it. Menu/report conditions are invoked from template expressions, so a
   * single change-detection pass can evaluate the same flag hundreds of times.
   *
   * We cache the *evaluated* value rather than the returned one: `defaultValue` varies per
   * call site, but the SDK only applies it when the evaluation yields `null`, so the flag
   * name alone is a correct key. `undefined` is a safe miss sentinel because a
   * `FeatureResult.value` is always `T | null`, never `undefined`.
   *
   * Invalidated whenever GrowthBook re-renders (feature refresh, attribute change, forced
   * values) via `setRenderer` below, and defensively in `setAttributes` for the window
   * before the renderer is installed.
   */
  private readonly evalCache = new Map<string, JSONValue | null>();

  attributes: Record<string, any> = {};

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private store: Store,
    private segmentService: SegmentService,
    private downloadService: DownloadService,
    private billingService: BillingService,
    private deviceDetector: DeviceDetector,
  ) {
    this.initialize();
    router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe((event: NavigationEnd) => {
      const urlTree = this.router.parseUrl(this.router.url);
      const tdInternal = urlTree.queryParams['td_internal']
        ?? this.activatedRoute.snapshot.queryParams['td_internal'];
      this.setAttributes({
        url_path: event.urlAfterRedirects,
        td_internal: tdInternal,
        automation: this.activatedRoute.snapshot.queryParams.automation,
        mobile_user: this.deviceDetector.isMobile(),
        browser: this.deviceDetector.browserDetectionName,
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async initialize() {
    const company$ = this.store.select(AuthState.company).pipe(distinctUntilChanged());

    this.store.select(AuthState.user).pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(user => {
      const domain = typeof user?.email === 'string' && user.email.includes('@')
        ? user.email.split('@')[1]
        : null;
      this.setAttributes({
        user_id: user?.id,
        email_type: this.segmentService.isFreeEmail(domain) ? 'free' : 'business',
      });
    });

    company$.pipe(
      takeUntil(this.destroy$),
    ).subscribe(company => {
      this.setAttributes({
        company_id: company?.id || '',
        pricing_plan: company?.pricingPlan || '',
        role: company?.role || '',
        company_created_date: company?.companyCreatedAt,
        current_date: new Date().toISOString(),
        tracking_mode: company?.companySettings?.trackingMode,
        user_tracking_mode: company?.userSettings?.trackingMode,
        billing_provider: company?.subscription?.provider,
        user_count: company?.userCount || 0,
        billing_access: company?.userSettings?.billingAccess,
        company_subscription_status: company?.subscription?.status,
        promoteBrowserApp: company?.companySettings?.custom?.splitTest?.promoteBrowserApp,
      });
    });

    company$.pipe(
      filter(company => company?.subscription?.provider === 'paddle'),
      switchMap(() => this.billingService.getBillingDetails().pipe(
        catchError(() => of(null)),
      )),
      takeUntil(this.destroy$),
    ).subscribe(billing => {
      this.setAttributes({
        subscription_status: billing?.customerStatus || '',
        billing_mode: billing?.billingMode || '',
      });
    });

    this.setAttributes({
      deployment: this.deployment,
      operating_system: this.downloadService.determineOS(),
    });

    (this.segmentService.traitsService?.companyTraits$ ?? EMPTY).pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(traits => {
      this.setAttributes({
        estimated_company_size: traits?.tdpeopleinthecompany,
        rdbms_status: traits?.status || '',
      });
    });

    this.instance.setRenderer(() => {
      this.evalCache.clear();
      this.rerenderCount.next(this.rerenderCount.value + 1);
    });

    if (clientKey) {
      await this.instance.loadFeatures({ autoRefresh: true });
    }

    this.initialized.next(true);
  }

  /**
   * Increments every time GrowthBook re-evaluates (feature refresh, attribute change,
   * forced values). Consumers that memoize flag-derived state can compare this to know
   * when their cache is stale, without subscribing.
   */
  get renderGeneration(): number {
    return this.rerenderCount.value;
  }

  /**
   * Get the value of a feature-flag in the current moment
   *
   * @example
   *
   * ```
   *   const result = this.growthbook.getFeatureValue('featureKey') === true
   * ```
   */
  getFeatureValue<T extends JSONValue>(feature: GrowthBookFeature, defaultValue?: T) {
    let value = this.evalCache.get(feature);
    if (value === undefined) {
      value = this.instance.evalFeature(feature).value;
      this.evalCache.set(feature, value);
    }
    return (value === null ? defaultValue : value) as WidenPrimitives<T>;
  }

  /**
   * Get the reactive value of a feature-flag
   *
   * @example
   *
   * ```
   *   this.growthbook.getFeature('featureKey').subscribe((value) => console.log(value))
   * ```
   */
  getFeature<T extends JSONValue>(feature: GrowthBookFeature, defaultValue?: T) {
    return combineLatest([this.initialized, this.rerenderCount]).pipe(
      map(() => this.getFeatureValue<T>(feature, defaultValue)),
      distinctUntilChanged(),
    );
  }

  setAttribute(key: string, value: any) {
    this.setAttributes({ [key]: value });
  }

  setAttributes(updates: Record<string, any>) {
    let changed = false;
    for (const key of Object.keys(updates)) {
      if (this.attributes[key] !== updates[key]) {
        this.attributes[key] = updates[key];
        changed = true;
      }
    }
    if (changed) {
      this.evalCache.clear();
      this.instance.setAttributes(this.attributes);
    }
  }

}
